import React, { useState, useMemo } from 'react';
import { XMarkIcon, ExclamationTriangleIcon } from '@heroicons/react/24/outline';
import { addBpcTenant, updateBpcTenant } from '../api/client';
import { useAuth } from '../hooks/useAuth';

const GUID_REGEX = /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/;

export default function EditBpcTenantDrawer({ tenant, m365Tenants, existingBpcIds, onClose, onSaved }) {
  const isEdit = !!tenant;

  const { user } = useAuth();
  const isGlobalAdmin = !!user?.isGlobalAdmin;
  const adminMspCodes = user?.adminMspCodes || [];
  const mspCatalog = user?.mspCatalog || [];

  // MSP options: global admins choose any catalog MSP; MSP admins are restricted to
  // the MSP(s) they administer. On edit, a non-global admin cannot reassign the MSP
  // (server enforces this too), so the select is locked to the row's current value.
  let mspOptions;
  if (isGlobalAdmin) {
    mspOptions = mspCatalog.length > 0 ? mspCatalog : adminMspCodes.map((c) => ({ code: c, displayName: c }));
  } else if (mspCatalog.length > 0) {
    const set = new Set(adminMspCodes.map((c) => (c || '').toUpperCase()));
    mspOptions = mspCatalog.filter((m) => set.has((m.code || '').toUpperCase()));
    if (mspOptions.length === 0) mspOptions = adminMspCodes.map((c) => ({ code: c, displayName: c }));
  } else {
    mspOptions = adminMspCodes.map((c) => ({ code: c, displayName: c }));
  }

  const [bpcTenantId,        setBpcTenantId]        = useState(tenant?.bpcTenantId        || '');
  const [bpcTenantName,      setBpcTenantName]      = useState(tenant?.bpcTenantName      || '');
  const [bpcAccountId,       setBpcAccountId]       = useState(tenant?.bpcAccountId       || '');
  const [bpcAccountName,     setBpcAccountName]     = useState(tenant?.bpcAccountName     || '');
  const [keyVaultSecretName, setKeyVaultSecretName] = useState(tenant?.keyVaultSecretName || '');
  const [m365TenantId,       setM365TenantId]       = useState(tenant?.m365TenantId       || '');
  const [msp,                setMsp]                = useState(tenant?.msp || (isGlobalAdmin ? '' : (adminMspCodes[0] || '')));
  const [notes,              setNotes]              = useState(tenant?.notes              || '');

  // Non-global admins cannot move an existing row to a different MSP.
  const mspLocked = isEdit && !isGlobalAdmin;

  const [saving, setSaving] = useState(false);
  const [error,  setError]  = useState(null);

  const sortedM365 = useMemo(() => {
    return [...(m365Tenants || [])]
      .filter((t) => t.tenantId)
      .sort((a, b) => (a.friendlyName || '').localeCompare(b.friendlyName || ''));
  }, [m365Tenants]);

  // Validation
  const validate = () => {
    if (!bpcTenantId.trim())                            return 'BPC Tenant ID is required.';
    if (!GUID_REGEX.test(bpcTenantId.trim()))           return 'BPC Tenant ID must be a GUID.';
    if (!isEdit && existingBpcIds?.includes(bpcTenantId.trim())) {
      return 'A BPC tenant with this ID already exists.';
    }
    if (bpcAccountId && !GUID_REGEX.test(bpcAccountId.trim())) return 'BPC Account ID must be a GUID (or empty).';
    if (m365TenantId && !GUID_REGEX.test(m365TenantId.trim())) return 'M365 Tenant ID must be a GUID (or empty).';
    if (!bpcTenantName.trim())                          return 'BPC Tenant Name is required.';
    if (!msp.trim())                                    return 'MSP is required.';
    return null;
  };

  const handleSave = async () => {
    const v = validate();
    if (v) { setError(v); return; }

    setSaving(true);
    setError(null);
    try {
      const payload = {
        bpcTenantName:      bpcTenantName.trim(),
        bpcAccountId:       bpcAccountId.trim(),
        bpcAccountName:     bpcAccountName.trim(),
        keyVaultSecretName: keyVaultSecretName.trim(),
        m365TenantId:       m365TenantId.trim(),
        msp:                msp.trim().toUpperCase(),
        notes:              notes,
      };
      if (isEdit) {
        await updateBpcTenant(tenant.bpcTenantId, payload);
      } else {
        await addBpcTenant({ bpcTenantId: bpcTenantId.trim(), ...payload });
      }
      onSaved();
    } catch (err) {
      setError(err.message || 'Failed to save BPC tenant.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-40 flex">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/60" onClick={saving ? undefined : onClose} />

      {/* Drawer */}
      <div className="relative ml-auto w-full max-w-md bg-gray-900 border-l border-gray-800 shadow-2xl flex flex-col h-full">
        <div className="flex items-center justify-between px-5 py-3 border-b border-gray-800">
          <h2 className="text-base font-semibold text-gray-100">
            {isEdit ? 'Edit BPC Tenant' : 'Add BPC Tenant'}
          </h2>
          <button onClick={onClose} disabled={saving} className="text-gray-500 hover:text-gray-300 p-1">
            <XMarkIcon className="h-5 w-5" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-5 py-4 space-y-4 text-sm">

          {/* BPC Tenant ID */}
          <div>
            <label className="block text-xs font-medium text-gray-400 mb-1">
              BPC Tenant ID <span className="text-red-400">*</span>
            </label>
            <input
              type="text"
              value={bpcTenantId}
              onChange={(e) => setBpcTenantId(e.target.value)}
              disabled={isEdit || saving}
              placeholder="e.g. b7ce619e-f5b6-46a1-80bf-ea78e359fdb9"
              className="input-field text-sm w-full font-mono"
            />
            {isEdit && <p className="mt-1 text-[11px] text-gray-600">BPC Tenant ID is immutable.</p>}
          </div>

          {/* BPC Tenant Name */}
          <div>
            <label className="block text-xs font-medium text-gray-400 mb-1">
              BPC Tenant Name <span className="text-red-400">*</span>
            </label>
            <input
              type="text"
              value={bpcTenantName}
              onChange={(e) => setBpcTenantName(e.target.value)}
              disabled={saving}
              placeholder="e.g. Blue Alliance (All)"
              className="input-field text-sm w-full"
            />
          </div>

          {/* BPC Account fields — pipeline columns */}
          <div className="border border-gray-800 rounded-md p-3 space-y-3 bg-gray-900/40">
            <p className="text-[11px] font-medium text-gray-500 uppercase tracking-wide">BPC Account (MSP-level)</p>
            <div>
              <label className="block text-xs font-medium text-gray-400 mb-1">
                MSP <span className="text-red-400">*</span>
              </label>
              {mspOptions.length > 0 ? (
                <select
                  value={msp}
                  onChange={(e) => setMsp(e.target.value)}
                  disabled={saving || mspLocked}
                  className="input-field text-sm w-full"
                >
                  <option value="">— Select MSP —</option>
                  {/* When locked on edit, ensure the current value is selectable even if not in options */}
                  {mspLocked && msp && !mspOptions.some((m) => (m.code || '').toUpperCase() === msp.toUpperCase()) && (
                    <option value={msp}>{msp}</option>
                  )}
                  {mspOptions.map((m) => (
                    <option key={m.code} value={m.code}>
                      {m.displayName ? `${m.displayName} (${m.code})` : m.code}
                    </option>
                  ))}
                </select>
              ) : (
                <input
                  type="text"
                  value={msp}
                  onChange={(e) => setMsp(e.target.value)}
                  disabled={saving || mspLocked}
                  placeholder="e.g. PFIT"
                  className="input-field text-sm w-full"
                />
              )}
              {mspLocked && (
                <p className="mt-1 text-[11px] text-gray-600">Only a global admin can move this BPC tenant to a different MSP.</p>
              )}
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-400 mb-1">BPC Account Name</label>
              <input
                type="text"
                value={bpcAccountName}
                onChange={(e) => setBpcAccountName(e.target.value)}
                disabled={saving}
                placeholder="e.g. BlueAllianceIT"
                className="input-field text-sm w-full"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-400 mb-1">BPC Account ID</label>
              <input
                type="text"
                value={bpcAccountId}
                onChange={(e) => setBpcAccountId(e.target.value)}
                disabled={saving}
                placeholder="MSP-level account GUID (optional)"
                className="input-field text-sm w-full font-mono"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-400 mb-1">Key Vault Secret Name</label>
              <input
                type="text"
                value={keyVaultSecretName}
                onChange={(e) => setKeyVaultSecretName(e.target.value)}
                disabled={saving}
                placeholder="e.g. BpcApiKey-BlueAllianceIT"
                className="input-field text-sm w-full font-mono"
              />
              <p className="mt-1 text-[11px] text-gray-600">
                Secret in M365Signal-KeyVault that holds the BPC API key for this account. Pipeline-managed; rarely changes.
              </p>
            </div>
          </div>

          {/* M365 mapping */}
          <div>
            <label className="block text-xs font-medium text-gray-400 mb-1">M365 Tenant Mapping</label>
            <select
              value={m365TenantId}
              onChange={(e) => setM365TenantId(e.target.value)}
              disabled={saving}
              className="input-field text-sm w-full"
            >
              <option value="">— Unmapped —</option>
              {sortedM365.map((t) => (
                <option key={t.tenantId} value={t.tenantId}>
                  {t.friendlyName || t.domain || t.tenantId}
                </option>
              ))}
            </select>
            <p className="mt-1 text-[11px] text-gray-600">
              When mapped, the Device Inventory Comparison page can scope BPC devices for this M365 tenant.
            </p>
          </div>

          {/* Notes */}
          <div>
            <label className="block text-xs font-medium text-gray-400 mb-1">Notes</label>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              disabled={saving}
              rows={3}
              className="input-field text-sm w-full"
            />
          </div>

          {/* Audit info on edit */}
          {isEdit && (tenant.addedUtc || tenant.updatedUtc) && (
            <div className="text-[11px] text-gray-600 border-t border-gray-800 pt-3 space-y-0.5">
              {tenant.addedUtc && <div>Added: {tenant.addedUtc} {tenant.addedBy && `by ${tenant.addedBy}`}</div>}
              {tenant.updatedUtc && <div>Updated: {tenant.updatedUtc} {tenant.updatedBy && `by ${tenant.updatedBy}`}</div>}
            </div>
          )}

          {/* Error */}
          {error && (
            <div className="flex items-start gap-2 p-3 bg-red-900/30 border border-red-700/50 rounded text-xs text-red-300">
              <ExclamationTriangleIcon className="h-4 w-4 flex-shrink-0 text-red-400 mt-0.5" />
              <span>{error}</span>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex justify-end gap-2 px-5 py-3 border-t border-gray-800">
          <button onClick={onClose} disabled={saving} className="btn-secondary text-sm">Cancel</button>
          <button onClick={handleSave} disabled={saving} className="btn-primary text-sm">
            {saving ? 'Saving…' : (isEdit ? 'Save Changes' : 'Add BPC Tenant')}
          </button>
        </div>
      </div>
    </div>
  );
}
